import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendUp, Warning, CircleNotch, ArrowsOut, ArrowsIn, Brain } from "@phosphor-icons/react";
import Sparkline from "./Sparkline";
import FundamentalsPanel from "./FundamentalsPanel";
import CandleChart from "./CandleChart";
import ChartDrawingOverlay from "./ChartDrawingOverlay";
import IndicatorMenu from "./IndicatorMenu";
import AdminEditTradePlan from "./AdminEditTradePlan";
import PositionSizer from "./PositionSizer";
import ChartAnalysisPanel from "./ChartAnalysisPanel";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useLivePrices, { bareSymbol } from "../hooks/useLivePrices";
import useMarketHours from "../hooks/useMarketHours";

// Each button represents the CANDLE INTERVAL (TradingView-style).
// The chart loads as much history as the data source permits (yfinance
// caps intraday windows; daily/weekly/monthly go all the way back to
// listing).
const TIMEFRAMES = [
  { k: "1m",  label: "1m",  interval: "1m",  period: "5d" },        // yfinance cap 7d
  { k: "5m",  label: "5m",  interval: "5m",  period: "1mo" },       // yfinance cap 60d
  { k: "15m", label: "15m", interval: "15m", period: "1mo" },       // yfinance cap 60d
  { k: "1h",  label: "1H",  interval: "1h",  period: "2y" },        // yfinance cap 730d
  { k: "1d",  label: "1D",  interval: "1d",  period: "max" },       // full history
  { k: "1wk", label: "1W",  interval: "1wk", period: "max" },
  { k: "1mo", label: "1M",  interval: "1mo", period: "max" },
];

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : "—";

// Builds a simple line path + entry/SL/target guideline overlay for the sparkline block
function BigChart({ data = [], entry, sl, target, ema10 }) {
  if (!data.length) return null;
  const w = 760;
  const h = 280;
  const pad = 20;
  const min = Math.min(...data, sl || Infinity, ema10 || Infinity);
  const max = Math.max(...data, target || -Infinity, ema10 || -Infinity);
  const range = max - min || 1;
  const y = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
  const step = (w - pad * 2) / Math.max(1, data.length - 1);
  const pts = data.map((v, i) => `${pad + i * step},${y(v)}`).join(" ");

  const line = (val, color, dashed, label) => (
    <g key={label}>
      <line
        x1={pad}
        x2={w - pad}
        y1={y(val)}
        y2={y(val)}
        stroke={color}
        strokeWidth="1"
        strokeDasharray={dashed ? "3 4" : "0"}
        opacity="0.8"
      />
      <text x={w - pad + 4} y={y(val) + 3} fill={color} fontSize="10" fontFamily="JetBrains Mono">
        {label}
      </text>
    </g>
  );

  return (
    <svg viewBox={`0 0 ${w + 40} ${h}`} width="100%" className="block text-primary-fg">
      <rect x="0" y="0" width={w + 40} height={h} fill="transparent" />
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={pts} />
      {entry != null && line(entry, "hsl(160 84% 39%)", false, `Entry ₹${fmt(entry)}`)}
      {sl != null && line(sl, "hsl(0 84% 60%)", true, `SL ₹${fmt(sl)}`)}
      {target != null && line(target, "hsl(160 84% 39%)", true, `Target ₹${fmt(target)}`)}
      {ema10 != null && line(ema10, "hsl(38 92% 50%)", true, `EMA10 ₹${fmt(ema10)}`)}
    </svg>
  );
}



export default function StockDetailModal({ signal, onClose, onWatch, watched, onUpdated, onReloadWatchlist }) {
  const [ohlc, setOhlc] = useState(null);
  const [ohlcLoading, setOhlcLoading] = useState(false);
  // Subscribe to live prices (1-min) for the open stock. Silently no-op
  // when the symbol isn't in the Upstox snapshot (low-volume names) —
  // header keeps showing the EOD/scan price as fallback.
  const liveSymList = React.useMemo(
    () => (signal?.symbol ? [bareSymbol(signal.symbol)] : []),
    [signal?.symbol]
  );
  const { prices: livePricesMap } = useLivePrices(liveSymList, 60000);
  const livePrice = signal?.symbol ? livePricesMap[bareSymbol(signal.symbol)] : null;
  // Chart controls — timeframe selector (`tfKey` = key in TIMEFRAMES)
  // + fullscreen toggle + Nifty comparison overlay. Default: 6-month daily.
  const [tfKey, setTfKey] = useState("1d");
  const [fullscreen, setFullscreen] = useState(false);
  const [withNifty, setWithNifty] = useState(false);
  const [niftyOhlc, setNiftyOhlc] = useState(null);
  const [withSector, setWithSector] = useState(false);
  const [sectorMeta, setSectorMeta] = useState(null); // {symbol, name}
  const [sectorOhlc, setSectorOhlc] = useState(null);
  // Multi-period RS data: independent of the chart timeframe. We pull a
  // single 1-year daily series for stock + Nifty + sector and slice it to
  // produce 1W / 1M / 3M / 6M / 1Y deltas. Fetched once per symbol.
  const [rsStripBars, setRsStripBars] = useState({ stock: null, nifty: null, sector: null });
  // Drawing tools — Trendline / Ruler / Fibonacci
  // `chartCtx` is set via CandleChart's `onReady` callback and exposes
  // the chart + main candle series so the overlay can do pixel↔price/time
  // coordinate translation. Drawings are persisted per (user, symbol,
  // timeframe) in MongoDB via /api/drawings — so they sync across devices.
  // localStorage is kept as an offline-first cache so the UI feels instant
  // and survives temporary backend hiccups.
  const [activeTool, setActiveTool] = useState(null);
  const [drawings, setDrawings] = useState([]);
  // AI Chart Analysis (premium) — toggled by the "Analyze Chart" button.
  // When `analysis` is set, the chart overlays dashed S/R lines and the
  // ChartAnalysisPanel renders below the toolbar. Closing the panel clears
  // both the text and the lines.
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState("");
  const [analysisMeta, setAnalysisMeta] = useState(null);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  // Pinned S/R levels — persisted per (user, symbol). These survive a
  // page reload and show on the chart even when the analysis panel is
  // closed.
  const [pinnedLevels, setPinnedLevels] = useState([]);
  // Technical indicators currently shown on the chart. Stored as an
  // array (so we can pass it directly as a stable prop) but treated as
  // a set in CandleChart. Persisted in localStorage so the user's
  // preferred indicators survive reloads.
  const [activeIndicators, setActiveIndicators] = useState(() => {
    try {
      const raw = window.localStorage.getItem("chart-indicators");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("chart-indicators", JSON.stringify(activeIndicators)); }
    catch { /* ignore */ }
  }, [activeIndicators]);
  const toggleIndicator = (k) =>
    setActiveIndicators((arr) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]));
  // Per-indicator parameters (period, multiplier, MACD bands etc.).
  // Sparse object — only keys with user-modified values are present;
  // CandleChart merges them on top of its defaults.
  const [indicatorParams, setIndicatorParams] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("chart-indicator-params") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("chart-indicator-params", JSON.stringify(indicatorParams)); }
    catch { /* ignore */ }
  }, [indicatorParams]);
  const [showSettings, setShowSettings] = useState(false);
  // Saved indicator presets — each {id, name, indicators, params, is_default}.
  // Persisted in MongoDB (cross-device) via /api/indicator-presets, with
  // localStorage as an offline-first cache for instant paint.
  const [presets, setPresets] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem("chart-indicator-presets") || "[]"); }
    catch { return []; }
  });
  const presetsLoadedRef = useRef(false);
  // Initial fetch from server (authoritative). Auto-apply default preset on
  // first load if the user hasn't manually selected any indicators yet —
  // this is the personalised "welcome back" touch.
  useEffect(() => {
    api.get("/indicator-presets").then(({ data }) => {
      const list = Array.isArray(data?.presets) ? data.presets : [];
      setPresets(list);
      try { window.localStorage.setItem("chart-indicator-presets", JSON.stringify(list)); } catch { /* */ }
      if (!presetsLoadedRef.current) {
        presetsLoadedRef.current = true;
        const def = list.find((p) => p.is_default);
        if (def && activeIndicators.length === 0) {
          setActiveIndicators([...def.indicators]);
          setIndicatorParams({ ...def.params });
        }
      }
    }).catch(() => { /* offline → keep cached copy */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const savePreset = async () => {
    if (activeIndicators.length === 0) return;
    const name = window.prompt("Preset name (e.g. \"9/21 EMA + RSI 7\")");
    if (!name) return;
    try {
      const { data } = await api.post("/indicator-presets", {
        name: name.trim().slice(0, 60),
        indicators: [...activeIndicators],
        params: { ...indicatorParams },
      });
      setPresets((p) => {
        const next = [...p, data];
        try { window.localStorage.setItem("chart-indicator-presets", JSON.stringify(next)); } catch { /* */ }
        return next;
      });
    } catch {
      window.alert("Couldn't save preset — please try again.");
    }
  };
  const applyPreset = (preset) => {
    setActiveIndicators([...preset.indicators]);
    setIndicatorParams({ ...preset.params });
  };
  const deletePreset = async (id) => {
    try { await api.delete(`/indicator-presets/${encodeURIComponent(id)}`); } catch { /* */ }
    setPresets((p) => {
      const next = p.filter((x) => x.id !== id);
      try { window.localStorage.setItem("chart-indicator-presets", JSON.stringify(next)); } catch { /* */ }
      return next;
    });
  };
  const toggleDefaultPreset = async (id) => {
    try {
      const { data } = await api.patch(`/indicator-presets/${encodeURIComponent(id)}/default`);
      setPresets((p) => {
        const next = p.map((x) =>
          x.id === id ? { ...x, is_default: data.is_default } : { ...x, is_default: false },
        );
        try { window.localStorage.setItem("chart-indicator-presets", JSON.stringify(next)); } catch { /* */ }
        return next;
      });
    } catch { /* */ }
  };
  const updateParam = (key, field, raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    setIndicatorParams((p) => ({ ...p, [key]: { ...(p[key] || {}), [field]: n } }));
  };
  const resetParams = (key) =>
    setIndicatorParams((p) => {
      const cp = { ...p };
      delete cp[key];
      return cp;
    });
  const chartCtxRef = useRef(null);
  const drawingsKey = signal?.symbol ? `chart-drawings:${signal.symbol}:${tfKey}` : null;
  // Skip the first save-effect right after we load from server, so we don't
  // bounce the freshly-loaded data straight back as a PUT.
  const skipNextSave = useRef(false);

  // Load: cache-first (instant paint), then server (authoritative + cross-device sync).
  useEffect(() => {
    setActiveTool(null);
    if (!signal?.symbol || !drawingsKey) { setDrawings([]); return; }
    let cancelled = false;
    // 1) Hydrate from localStorage immediately (no flicker)
    try {
      const raw = window.localStorage.getItem(drawingsKey);
      skipNextSave.current = true;
      setDrawings(raw ? JSON.parse(raw) : []);
    } catch {
      skipNextSave.current = true;
      setDrawings([]);
    }
    // 2) Fetch from server and overwrite (server wins on conflict)
    api
      .get(`/drawings/${encodeURIComponent(signal.symbol)}?tf=${encodeURIComponent(tfKey)}`)
      .then(({ data }) => {
        if (cancelled) return;
        skipNextSave.current = true;
        setDrawings(Array.isArray(data?.drawings) ? data.drawings : []);
      })
      .catch(() => { /* offline → keep localStorage copy */ });
    return () => { cancelled = true; };
  }, [signal?.symbol, drawingsKey, tfKey]);

  // Persist: always update localStorage (offline cache) + push to server.
  useEffect(() => {
    if (!signal?.symbol || !drawingsKey) return;
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    // local cache
    try {
      if (drawings.length === 0) window.localStorage.removeItem(drawingsKey);
      else window.localStorage.setItem(drawingsKey, JSON.stringify(drawings));
    } catch { /* quota / disabled — ignore */ }
    // server sync (debounced via cleanup so rapid edits coalesce)
    const t = setTimeout(() => {
      const url = `/drawings/${encodeURIComponent(signal.symbol)}?tf=${encodeURIComponent(tfKey)}`;
      if (drawings.length === 0) {
        api.delete(url).catch(() => {});
      } else {
        api.put(url, { drawings }).catch(() => {});
      }
    }, 350);
    return () => clearTimeout(t);
  }, [signal?.symbol, drawingsKey, tfKey, drawings]);
  const tf = TIMEFRAMES.find((t) => t.k === tfKey) || TIMEFRAMES[4];
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAnalyze = isAdmin || !!user?.is_premium;
  const marketOpen = useMarketHours();

  // Reset analysis whenever the user switches symbol or timeframe — stale
  // S/R levels would be misleading on a different chart.
  useEffect(() => {
    setAnalysis(null);
    setAnalysisMeta(null);
    setAnalysisError("");
  }, [signal?.symbol, tfKey]);

  // Load pinned S/R levels for this symbol on open / symbol change.
  // These render on the chart even when the analysis panel is closed.
  useEffect(() => {
    if (!signal?.symbol || !canAnalyze) {
      setPinnedLevels([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/chart/pinned-levels", {
          params: { symbol: bareSymbol(signal.symbol) },
        });
        if (!cancelled) setPinnedLevels(data?.items || []);
      } catch {
        if (!cancelled) setPinnedLevels([]);
      }
    })();
    return () => { cancelled = true; };
  }, [signal?.symbol, canAnalyze]);

  // Set of `${kind}|${price}` keys for the analysis panel to highlight
  // already-pinned rows.
  const pinnedKeys = React.useMemo(() => {
    const s = new Set();
    pinnedLevels.forEach((p) => s.add(`${p.kind}|${Number(p.price).toFixed(2)}`));
    return s;
  }, [pinnedLevels]);

  // Derive dashed S/R levels that CandleChart will render. Pinned levels
  // are always included; AI analysis levels join them while the panel
  // is open. Duplicates (same kind+price) are de-duped.
  const srLevels = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (price, strength, kind, pinned = false) => {
      if (typeof price !== "number") return;
      const key = `${kind}|${price.toFixed(2)}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ price, strength, kind, pinned });
    };
    pinnedLevels.forEach((p) => push(p.price, p.strength, p.kind, true));
    if (analysis) {
      (analysis.supports || []).forEach((s) => push(s.price, s.strength, "support"));
      (analysis.resistances || []).forEach((r) => push(r.price, r.strength, "resistance"));
    }
    return out;
  }, [analysis, pinnedLevels]);

  const runChartAnalysis = async (force = false) => {
    if (!canAnalyze || !signal?.symbol) return;
    const bars = ohlc?.bars;
    if (!Array.isArray(bars) || bars.length < 30) {
      setAnalysisError("Chart data still loading — try again in a moment.");
      return;
    }
    setAnalysisLoading(true);
    setAnalysisError("");
    try {
      const { data } = await api.post("/chart/analyze", {
        symbol: bareSymbol(signal.symbol),
        timeframe: tfKey,
        bars,
        force,
      });
      setAnalysis(data?.result || null);
      setAnalysisMeta({ generated_at: data?.generated_at, cached: data?.cached });
    } catch (e) {
      const msg = e?.response?.status === 402
        ? "AI Chart Analysis is a premium feature."
        : e?.response?.data?.detail || "Couldn't analyse chart right now.";
      setAnalysisError(typeof msg === "string" ? msg : "Couldn't analyse chart right now.");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const closeChartAnalysis = () => {
    setAnalysis(null);
    setAnalysisMeta(null);
    setAnalysisError("");
  };

  /** Compose a short, human-readable AI note that fits in a row caption. */
  const composeAiNote = (a) => {
    if (!a) return "";
    const parts = [`AI · ${a.bias || "Neutral"}`];
    const topSup = (a.supports || [])[0];
    const topRes = (a.resistances || [])[0];
    if (topSup?.price) parts.push(`S ₹${topSup.price}`);
    if (topRes?.price) parts.push(`R ₹${topRes.price}`);
    if (a.entry_zone?.low && a.entry_zone?.high) {
      parts.push(`zone ₹${a.entry_zone.low}–₹${a.entry_zone.high}`);
    }
    const day = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    return `${parts.join(" · ")} — ${day}`;
  };

  const saveAiNoteToWatchlist = async () => {
    if (!analysis || !signal?.symbol) return;
    const note = composeAiNote(analysis);
    await api.post("/watchlist", { symbol: signal.symbol, note });
    // Refresh the parent's watchlist so the new note shows up + the star
    // state updates without forcing the user to navigate.
    onReloadWatchlist?.();
  };

  /** Toggle a pin for the given level on the active symbol. Pins are
   * persistent (per-user, per-symbol) and survive panel close + page
   * reload. */
  const togglePinLevel = async (level) => {
    if (!signal?.symbol || !level) return;
    const sym = bareSymbol(signal.symbol);
    const priceRounded = Number(level.price).toFixed(2);
    const existing = pinnedLevels.find(
      (p) => p.kind === level.kind && Number(p.price).toFixed(2) === priceRounded
    );
    if (existing) {
      // Optimistic delete
      setPinnedLevels((arr) => arr.filter((p) => p.id !== existing.id));
      try {
        await api.delete(`/chart/pinned-levels/${existing.id}`);
      } catch {
        setPinnedLevels((arr) => [...arr, existing]);
      }
      return;
    }
    try {
      const { data } = await api.post("/chart/pinned-levels", {
        symbol: sym,
        price: Number(level.price),
        kind: level.kind,
        strength: level.strength,
        why: level.why,
        source: "ai",
      });
      if (data?.level) setPinnedLevels((arr) => [...arr, data.level]);
    } catch {
      /* silent — toast already shown by axios interceptor if any */
    }
  };

  // Local copy of editable fields so the user sees their edits immediately
  // even before re-fetching the parent's signal list.
  const [localPlan, setLocalPlan] = useState(null);
  useEffect(() => {
    setLocalPlan(null);
  }, [signal?.symbol, signal?.signal_type, signal?.first_detected_at]);

  const target = localPlan?.target ?? signal?.target;
  const stop_loss = localPlan?.stop_loss ?? signal?.stop_loss;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (fullscreen) setFullscreen(false);
        else onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, fullscreen]);

  useEffect(() => {
    if (!signal?.symbol) return;
    setOhlc(null);
    setOhlcLoading(true);
    api
      .get(
        `/ohlc/${encodeURIComponent(signal.symbol)}?period=${tf.period}&interval=${tf.interval}`,
      )
      .then(({ data }) => setOhlc(data))
      .catch(() => setOhlc(null))
      .finally(() => setOhlcLoading(false));
  }, [signal?.symbol, tf.period, tf.interval]);

  // Fetch Nifty 50 OHLC unconditionally — used for both the comparison
  // overlay (rendered only when `withNifty` is true) and the RS badge in
  // the header (always visible).
  useEffect(() => {
    setNiftyOhlc(null);
    api
      .get(`/ohlc/${encodeURIComponent("^NSEI")}?period=${tf.period}&interval=${tf.interval}`)
      .then(({ data }) => setNiftyOhlc(data))
      .catch(() => setNiftyOhlc(null));
  }, [tf.period, tf.interval]);

  // Lookup sector index for the current symbol — only once per symbol.
  useEffect(() => {
    setSectorMeta(null);
    if (!signal?.symbol) return;
    api
      .get(`/symbol/${encodeURIComponent(signal.symbol)}/sector`)
      .then(({ data }) => setSectorMeta(data?.sector || null))
      .catch(() => setSectorMeta(null));
  }, [signal?.symbol]);

  // Fetch sector index OHLC unconditionally (once sector known) — feeds
  // both the overlay (toggled by `withSector`) and the RS header badge.
  useEffect(() => {
    setSectorOhlc(null);
    if (!sectorMeta?.symbol) return;
    api
      .get(`/ohlc/${encodeURIComponent(sectorMeta.symbol)}?period=${tf.period}&interval=${tf.interval}`)
      .then(({ data }) => setSectorOhlc(data))
      .catch(() => setSectorOhlc(null));
  }, [sectorMeta?.symbol, tf.period, tf.interval]);

  // RS strip data — single 1Y daily fetch per benchmark, sliced into
  // multiple periods (1W, 1M, 3M, 6M, 1Y). Independent of chart tf so the
  // strip stays stable as the user explores different chart timeframes.
  useEffect(() => {
    setRsStripBars({ stock: null, nifty: null, sector: null });
    if (!signal?.symbol) return;
    const cancelled = { v: false };
    const fetchBars = (sym) =>
      api
        .get(`/ohlc/${encodeURIComponent(sym)}?period=1y&interval=1d`)
        .then(({ data }) => (Array.isArray(data?.bars) ? data.bars : null))
        .catch(() => null);
    (async () => {
      const [s, n] = await Promise.all([fetchBars(signal.symbol), fetchBars("^NSEI")]);
      if (cancelled.v) return;
      setRsStripBars((prev) => ({ ...prev, stock: s, nifty: n }));
    })();
    return () => { cancelled.v = true; };
  }, [signal?.symbol]);

  // Fetch sector 1Y bars only after the sector mapping is resolved.
  useEffect(() => {
    if (!sectorMeta?.symbol) {
      setRsStripBars((prev) => ({ ...prev, sector: null }));
      return;
    }
    const cancelled = { v: false };
    api
      .get(`/ohlc/${encodeURIComponent(sectorMeta.symbol)}?period=1y&interval=1d`)
      .then(({ data }) => {
        if (cancelled.v) return;
        setRsStripBars((prev) => ({ ...prev, sector: Array.isArray(data?.bars) ? data.bars : null }));
      })
      .catch(() => {
        if (!cancelled.v) setRsStripBars((prev) => ({ ...prev, sector: null }));
      });
    return () => { cancelled.v = true; };
  }, [sectorMeta?.symbol]);

  // Relative-Strength badge: stock's % move minus index/sector's % move.
  // To stay 100% consistent with the multi-period RS strip below, the badge
  // is computed from the SAME 1Y daily series (`rsStripBars`) sliced at
  // the lookback matching the active chart timeframe. Older chart-window-
  // based math used different anchor logic and could disagree with the
  // strip's row for the same period.
  const TF_LOOKBACK_BARS = {
    "1m": 5, "5m": 5, "15m": 5, "1h": 22,
    "1d": 252, "1wk": 252, "1mo": 60,
  };
  const rs = (() => {
    const stock = rsStripBars.stock;
    if (!Array.isArray(stock) || stock.length < 2) return null;
    const lookback = TF_LOOKBACK_BARS[tfKey] || 132;
    const computeDelta = (series) => {
      if (!Array.isArray(series) || series.length < 2) return null;
      const sIdx = Math.max(0, series.length - 1 - lookback);
      const a = series[sIdx]?.close;
      const z = series[series.length - 1]?.close;
      if (!a || !z) return null;
      return ((z - a) / a) * 100;
    };
    const stockPct = computeDelta(stock);
    if (stockPct == null) return null;
    const niftyPct = computeDelta(rsStripBars.nifty);
    const sectorPct = computeDelta(rsStripBars.sector);
    return {
      stockPct,
      niftyDelta: niftyPct != null ? stockPct - niftyPct : null,
      sectorDelta: sectorPct != null ? stockPct - sectorPct : null,
    };
  })();

  // Multi-period RS strip — computed from the 1Y daily fetches, sliced
  // into 5 lookback windows. Returns rows for NIFTY (always) and the
  // sector (when available). Each cell is `stockReturn − benchmarkReturn`.
  const rsStrip = (() => {
    const stock = rsStripBars.stock;
    if (!Array.isArray(stock) || stock.length < 6) return null;
    const PERIODS = [
      { k: "1W", bars: 5 },
      { k: "1M", bars: 22 },
      { k: "3M", bars: 66 },
      { k: "6M", bars: 132 },
      { k: "1Y", bars: 252 },
    ];
    const lastClose = stock[stock.length - 1]?.close;
    if (!lastClose) return null;
    const deltaFor = (series, lookback) => {
      if (!Array.isArray(series) || series.length < 2) return null;
      const sIdx = Math.max(0, series.length - 1 - lookback);
      const startBar = series[sIdx];
      const endBar = series[series.length - 1];
      if (!startBar?.close || !endBar?.close) return null;
      return ((endBar.close - startBar.close) / startBar.close) * 100;
    };
    const rows = [];
    const stockRow = PERIODS.map((p) => deltaFor(stock, p.bars));
    if (rsStripBars.nifty) {
      const niftyRow = PERIODS.map((p) => deltaFor(rsStripBars.nifty, p.bars));
      rows.push({
        label: "NIFTY 50",
        deltas: stockRow.map((s, i) => (s != null && niftyRow[i] != null ? s - niftyRow[i] : null)),
      });
    }
    if (rsStripBars.sector && sectorMeta?.name) {
      const secRow = PERIODS.map((p) => deltaFor(rsStripBars.sector, p.bars));
      rows.push({
        label: sectorMeta.name.replace("NIFTY ", ""),
        deltas: stockRow.map((s, i) => (s != null && secRow[i] != null ? s - secRow[i] : null)),
      });
    }
    return { periods: PERIODS, rows };
  })();

  return (
    <AnimatePresence>
      {signal && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="stock-detail-modal"
        >
          <motion.div
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="relative bg-surface border border-subtle rounded-sm w-full max-w-5xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              data-testid="modal-close"
              className="absolute top-4 right-4 p-2 text-muted-fg hover:text-primary-fg hover:bg-surface-hover rounded-sm"
            >
              <X size={16} />
            </button>

            <div className="flex items-start justify-between pr-10 mb-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                  {signal._lookupOnly
                    ? "Chart Analysis · Lookup"
                    : signal.fno_only
                    ? "F&O Quick Look"
                    : signal.cup_mode
                    ? signal.kind === "long"
                      ? "Cup & Handle / Long · Monthly Confirmed"
                      : "Cup & Handle / Short · Weekly Confirmed"
                    : signal.signal_type === "long_term"
                    ? "Long-Term Breakout / > 3Y"
                    : "Short-Term Breakout / > 6M"}
                </div>
                <h3 className="font-mono text-3xl font-semibold tracking-wide text-primary-fg">
                  {signal.symbol.replace(".NS", "")}
                </h3>
                <div className="text-secondary-fg text-sm">{signal.company_name}</div>
                {/* Relative-Strength badge — shows how this stock's % move
                    over the visible window compares against Nifty 50 and
                    its sector index. Helps the trader instantly judge
                    leadership vs the broader market. */}
                {rs && (rs.niftyDelta != null || rs.sectorDelta != null) && (
                  <div
                    className="mt-2 flex items-center gap-3 flex-wrap"
                    data-testid="rs-badge"
                    title={`Relative performance over the visible chart window (${tf.label})`}
                  >
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
                      RS / {tf.label}
                    </span>
                    {rs.niftyDelta != null && (
                      <span
                        data-testid="rs-vs-nifty"
                        className={
                          "px-1.5 py-0.5 border rounded-sm text-[11px] font-mono tabular-nums " +
                          (rs.niftyDelta >= 0
                            ? "text-pos border-pos/40 bg-pos-soft"
                            : "text-neg border-neg/40 bg-neg-soft")
                        }
                      >
                        vs NIFTY {rs.niftyDelta >= 0 ? "+" : ""}{rs.niftyDelta.toFixed(1)}%
                      </span>
                    )}
                    {rs.sectorDelta != null && sectorMeta?.name && (
                      <span
                        data-testid="rs-vs-sector"
                        className={
                          "px-1.5 py-0.5 border rounded-sm text-[11px] font-mono tabular-nums " +
                          (rs.sectorDelta >= 0
                            ? "text-pos border-pos/40 bg-pos-soft"
                            : "text-neg border-neg/40 bg-neg-soft")
                        }
                      >
                        vs {sectorMeta.name.replace("NIFTY ", "")} {rs.sectorDelta >= 0 ? "+" : ""}{rs.sectorDelta.toFixed(1)}%
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">Last Price</div>
                {(() => {
                  // For lookup-only signals (any-NSE-search) we don't have a
                  // stored last_price — derive from the most recent daily bar.
                  // Live snapshot (1-min Upstox cache) wins when available.
                  let livePriceVal = signal.last_price;
                  let livePctVal = signal.pct_change_today;
                  if (livePrice && typeof livePrice.last_price === "number") {
                    livePriceVal = livePrice.last_price;
                    if (typeof livePrice.pct_change === "number") {
                      livePctVal = livePrice.pct_change;
                    }
                  }
                  if ((livePriceVal == null || !Number.isFinite(livePriceVal)) && Array.isArray(ohlc?.bars) && ohlc.bars.length >= 2) {
                    const last = ohlc.bars[ohlc.bars.length - 1];
                    const prev = ohlc.bars[ohlc.bars.length - 2];
                    livePriceVal = last.close;
                    if (prev?.close) livePctVal = ((last.close - prev.close) / prev.close) * 100;
                  }
                  const livePctNum = typeof livePctVal === "number" ? livePctVal : null;
                  const isLive = !!(livePrice && typeof livePrice.last_price === "number");
                  return (
                    <>
                      <div className="font-mono text-3xl tabular-nums text-primary-fg flex items-center justify-end gap-1.5">
                        ₹{fmt(livePriceVal)}
                        {isLive && (
                          <span
                            data-testid="modal-live-dot"
                            title="Live · refreshed every 60s"
                            className="inline-block w-1.5 h-1.5 rounded-full bg-pos animate-pulse"
                          />
                        )}
                      </div>
                      {livePctNum != null && (
                        <div
                          className={
                            "text-xs font-mono flex items-center justify-end gap-1 " +
                            (livePctNum >= 0 ? "text-pos" : "text-neg")
                          }
                          title={
                            isLive
                              ? "Live · 1-min Upstox snapshot · today % vs prev close"
                              : signal.last_price_refreshed_at
                              ? `Today's change vs prev close · refreshed ${new Date(signal.last_price_refreshed_at).toLocaleString("en-IN")}`
                              : "Today's change vs previous close"
                          }
                        >
                          <TrendUp size={12} weight="bold" className={livePctNum >= 0 ? "" : "rotate-180"} />
                          {livePctNum >= 0 ? "+" : ""}
                          {livePctNum.toFixed(2)}% today
                        </div>
                      )}
                    </>
                  );
                })()}
                {signal.fno_only ? (
                  signal.pct_change_price != null && (
                    <div
                      className={
                        "text-xs font-mono flex items-center justify-end gap-1 " +
                        (signal.pct_change_price >= 0 ? "text-pos" : "text-neg")
                      }
                    >
                      <TrendUp size={12} weight="bold" />
                      {signal.pct_change_price >= 0 ? "+" : ""}
                      {signal.pct_change_price.toFixed(2)}% today
                    </div>
                  )
                ) : signal.cup_mode ? (
                  signal.days_since_breakout != null && (
                    <div className="text-xs font-mono text-pos flex items-center justify-end gap-1">
                      <TrendUp size={12} weight="bold" />
                      Breakout {signal.days_since_breakout === 0 ? "today" : `${signal.days_since_breakout}d ago`}
                    </div>
                  )
                ) : (
                  signal.margin_pct != null && (
                    <div className="text-xs font-mono text-pos flex items-center justify-end gap-1">
                      <TrendUp size={12} weight="bold" /> +{signal.margin_pct.toFixed(2)}% vs breakout
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Multi-period RS strip — compact table showing how the stock
                has performed against NIFTY 50 and its sector over five
                lookback windows. Each cell = stock return − benchmark return,
                color-coded green (outperforming) or red (lagging). */}
            {rsStrip && rsStrip.rows.length > 0 && (
              <div
                className="mb-6 border border-subtle rounded-sm overflow-hidden"
                data-testid="rs-strip"
              >
                <div className="grid grid-cols-[120px_repeat(5,1fr)] text-[10px] font-mono uppercase tracking-widest bg-surface-hover">
                  <div className="px-3 py-2 text-muted-fg border-r border-subtle">
                    Relative · vs
                  </div>
                  {rsStrip.periods.map((p) => (
                    <div
                      key={p.k}
                      className="px-2 py-2 text-center text-muted-fg border-r border-subtle last:border-r-0"
                    >
                      {p.k}
                    </div>
                  ))}
                </div>
                {rsStrip.rows.map((row) => (
                  <div
                    key={row.label}
                    className="grid grid-cols-[120px_repeat(5,1fr)] border-t border-subtle"
                    data-testid={`rs-strip-row-${row.label}`}
                  >
                    <div className="px-3 py-2 font-mono text-xs text-secondary-fg border-r border-subtle flex items-center">
                      {row.label}
                    </div>
                    {row.deltas.map((d, i) => (
                      <div
                        key={rsStrip.periods[i].k}
                        className={
                          "px-2 py-2 font-mono text-[12px] tabular-nums text-center border-r border-subtle last:border-r-0 " +
                          (d == null
                            ? "text-muted-fg/40"
                            : d >= 0
                            ? "text-pos bg-pos-soft"
                            : "text-neg bg-neg-soft")
                        }
                      >
                        {d == null ? "—" : `${d >= 0 ? "+" : ""}${d.toFixed(1)}%`}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Price grid — only for breakout signals (F&O quick-look has no plan).
                R:R is computed from the live entry/SL/target so it always reflects
                admin edits in real-time without needing a server round-trip. */}
            {!signal.fno_only && !signal.cup_mode && !signal._lookupOnly && (() => {
              const e = Number(signal.entry || 0);
              const s = Number(stop_loss || 0);
              const t = Number(target || 0);
              const rr = e > 0 && s > 0 && s < e && t > e ? (t - e) / (e - s) : null;
              return (
              <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-subtle mb-6" data-testid="modal-price-grid">
              {[
                { l: "Entry", v: `₹${fmt(signal.entry)}`, c: "text-primary-fg" },
                { l: "Stop Loss", v: `₹${fmt(stop_loss)}`, c: "text-neg" },
                { l: "Target", v: `₹${fmt(target)}`, c: "text-pos" },
                { l: "Risk : Reward", v: rr ? `1 : ${rr.toFixed(2)}` : "—",
                  c: rr && rr >= 1.5 ? "text-pos" : rr ? "text-mod" : "text-muted-fg" },
                { l: "Breakout", v: `₹${fmt(signal.breakout_level)}`, c: "text-primary-fg" },
                { l: "EMA10", v: `₹${fmt(signal.ema10)}`, c: "text-mod" },
              ].map((it) => (
                <div key={it.l} className="p-4 border-r border-subtle last:border-r-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">
                    {it.l}
                  </div>
                  <div className={"font-mono text-lg tabular-nums " + it.c}>{it.v}</div>
                </div>
              ))}
              </div>
              );
            })()}

            {/* Admin-only Edit Trade Plan panel */}
            {isAdmin && !signal.fno_only && !signal.cup_mode && !signal._lookupOnly && signal.first_detected_at && (
              <div className="mb-6">
                <AdminEditTradePlan
                  signal={{
                    ...signal,
                    target,
                    stop_loss,
                    manually_edited: localPlan?.manually_edited ?? signal.manually_edited,
                    manually_edited_by: localPlan?.manually_edited_by ?? signal.manually_edited_by,
                    manually_edited_at: localPlan?.manually_edited_at ?? signal.manually_edited_at,
                  }}
                  endpoint="/admin/signals/trade-plan"
                  identifier={{
                    symbol: signal.symbol,
                    signal_type: signal.signal_type,
                    first_detected_at: signal.first_detected_at,
                  }}
                  onSaved={(updated) => {
                    setLocalPlan({
                      target: updated.target,
                      stop_loss: updated.stop_loss,
                      risk_reward: updated.risk_reward,
                      manually_edited: true,
                      manually_edited_at: updated.manually_edited_at,
                      manually_edited_by: updated.manually_edited_by,
                    });
                    onUpdated?.(updated);
                  }}
                />
              </div>
            )}

            {/* Admin-only Edit Trade Plan panel — Cup & Handle */}
            {isAdmin && signal.cup_mode && signal.breakout_date && (
              <div className="mb-6">
                <AdminEditTradePlan
                  signal={{
                    ...signal,
                    target,
                    stop_loss,
                    manually_edited: localPlan?.manually_edited ?? signal.manually_edited,
                    manually_edited_by: localPlan?.manually_edited_by ?? signal.manually_edited_by,
                    manually_edited_at: localPlan?.manually_edited_at ?? signal.manually_edited_at,
                  }}
                  endpoint="/admin/cup-signals/trade-plan"
                  identifier={{
                    symbol: signal.symbol,
                    kind: signal.kind,
                    breakout_date: signal.breakout_date,
                  }}
                  onSaved={(updated) => {
                    setLocalPlan({
                      target: updated.target,
                      stop_loss: updated.stop_loss,
                      risk_reward: updated.risk_reward,
                      manually_edited: true,
                      manually_edited_at: updated.manually_edited_at,
                      manually_edited_by: updated.manually_edited_by,
                    });
                    onUpdated?.(updated);
                  }}
                />
              </div>
            )}

            {/* Cup-and-Handle specific 5-cell grid: same shape as breakout
                grid but the labels reflect cup geometry */}
            {signal.cup_mode && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-subtle mb-6">
                {[
                  { l: "Entry (rim)", v: `₹${fmt(signal.entry)}`, c: "text-primary-fg" },
                  {
                    l: signal.kind === "long" ? "SL · Monthly low" : "SL · Weekly low",
                    v: `₹${fmt(signal.stop_loss)}`,
                    c: "text-neg",
                  },
                  { l: "Target", v: `₹${fmt(signal.target)}`, c: "text-pos" },
                  { l: "Cup low", v: `₹${fmt(signal.cup_low)}`, c: "text-mod" },
                  {
                    l: "Depth · Dur",
                    v: `${fmt(signal.cup_depth_pct, 1)}% · ${signal.cup_duration_days}d`,
                    c: "text-secondary-fg",
                  },
                ].map((it) => (
                  <div key={it.l} className="p-4 border-r border-subtle last:border-r-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1">
                      {it.l}
                    </div>
                    <div className={"font-mono text-base tabular-nums " + it.c}>{it.v}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Chart */}
            <div
              className={
                fullscreen
                  ? "fixed inset-0 z-[60] flex flex-col bg-app p-4"
                  : "border border-subtle p-4 bg-app"
              }
              data-testid="chart-card"
            >
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg min-w-0">
                  {signal.cup_mode
                    ? "Candles · EMA 10 · cup overlay (rim, low, breakout markers)"
                    : signal.ema_cross_mode
                    ? "Candles · EMA 10 (cyan) · EMA 20 (magenta) · crossover marker · volume"
                    : `Candles · EMA 10 · volume${!signal.fno_only ? " · entry/sl/target lines" : ""}`}
                </div>

                {/* Timeframe selector */}
                <div
                  className="flex items-center gap-1 flex-wrap"
                  data-testid="chart-tf-selector"
                >
                  {TIMEFRAMES.map((t) => {
                    const active = tfKey === t.k;
                    return (
                      <button
                        key={t.k}
                        type="button"
                        onClick={() => setTfKey(t.k)}
                        data-testid={`chart-tf-${t.k}`}
                        className={
                          "px-2 py-1 border rounded-sm text-[10px] font-mono tracking-widest transition " +
                          (active
                            ? "bg-pos text-bg border-pos"
                            : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-pos/40")
                        }
                      >
                        {t.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setWithNifty((v) => !v)}
                    data-testid="chart-nifty-toggle"
                    title={withNifty ? "Hide Nifty 50 overlay" : "Compare with Nifty 50"}
                    className={
                      "px-2 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1 " +
                      (withNifty
                        ? "bg-mod text-bg border-mod"
                        : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-mod/40")
                    }
                  >
                    + Nifty
                  </button>
                  {sectorMeta?.symbol && (
                    <button
                      type="button"
                      onClick={() => setWithSector((v) => !v)}
                      data-testid="chart-sector-toggle"
                      title={withSector ? `Hide ${sectorMeta.name} overlay` : `Compare with ${sectorMeta.name}`}
                      className={
                        "px-2 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1 " +
                        (withSector
                          ? "bg-purple-600 text-white border-purple-600"
                          : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-purple-500/40")
                      }
                    >
                      + {sectorMeta.name.replace("NIFTY ", "")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setFullscreen((f) => !f)}
                    data-testid="chart-fullscreen-toggle"
                    title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                    className="ml-1 p-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover hover:border-pos/40 transition"
                  >
                    {fullscreen ? (
                      <ArrowsIn size={12} weight="bold" />
                    ) : (
                      <ArrowsOut size={12} weight="bold" />
                    )}
                  </button>
                  {fullscreen && (
                    <button
                      type="button"
                      onClick={() => setFullscreen(false)}
                      data-testid="chart-fullscreen-close"
                      title="Close fullscreen"
                      className="p-1.5 border border-subtle rounded-sm text-secondary-fg hover:bg-surface-hover hover:border-neg/40 transition"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </div>

                {/* Drawing tools — Trendline / Ruler / Fibonacci.
                    Clicking a tool toggles it; pressing Esc cancels.
                    Performance: overlay only captures pointer events when a
                    tool is active, so panning stays smooth otherwise. */}
                <div
                  className="flex items-center gap-1 flex-wrap"
                  data-testid="chart-draw-toolbar"
                >
                  {[
                    { k: "trendline", label: "↗ Trend", color: "border-amber-500 bg-amber-500 text-black" },
                    { k: "ruler",     label: "⇕ Scale", color: "border-cyan-500 bg-cyan-500 text-black" },
                    { k: "fib",       label: "𝝫 Fib",   color: "border-violet-500 bg-violet-500 text-white" },
                  ].map((t) => {
                    const active = activeTool === t.k;
                    return (
                      <button
                        key={t.k}
                        type="button"
                        onClick={() => setActiveTool(active ? null : t.k)}
                        data-testid={`chart-draw-${t.k}`}
                        title={active ? `${t.label} active — click 2 points (Esc to cancel)` : `Draw ${t.label}`}
                        className={
                          "px-2 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                          (active
                            ? t.color
                            : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-pos/40")
                        }
                      >
                        {t.label}
                      </button>
                    );
                  })}
                  {drawings.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setDrawings((d) => d.slice(0, -1))}
                        data-testid="chart-draw-undo"
                        title="Undo last drawing"
                        className="px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:border-neg/40 transition"
                      >
                        Undo
                      </button>
                      <button
                        type="button"
                        onClick={() => { setDrawings([]); setActiveTool(null); }}
                        data-testid="chart-draw-clear"
                        title="Clear all drawings"
                        className="px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:border-neg/40 transition"
                      >
                        Clear
                      </button>
                    </>
                  )}
                  {/* AI Chart Analysis trigger — premium-gated. */}
                  {canAnalyze && (
                    <button
                      type="button"
                      onClick={() => (analysis ? closeChartAnalysis() : runChartAnalysis(false))}
                      disabled={analysisLoading}
                      data-testid="chart-analyze-btn"
                      title={
                        analysis
                          ? "Close analysis · clears S/R lines"
                          : "AI-powered technical analysis with auto-drawn S/R levels"
                      }
                      className={
                        "px-2 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition flex items-center gap-1 disabled:opacity-50 " +
                        (analysis
                          ? "border-pos text-pos bg-pos-soft/40"
                          : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-pos/60 hover:text-pos")
                      }
                    >
                      {analysisLoading ? (
                        <CircleNotch size={11} weight="bold" className="animate-spin" />
                      ) : (
                        <Brain size={11} weight={analysis ? "fill" : "regular"} />
                      )}
                      {analysis ? "Hide Analysis" : "Analyze Chart"}
                    </button>
                  )}
                </div>

                {!signal.fno_only && !signal.cup_mode && signal.volume_multiple != null && !fullscreen && (
                  <div className="text-xs font-mono text-secondary-fg">
                    Volume <span className="text-primary-fg">{signal.volume_multiple}×</span> avg
                  </div>
                )}
                {signal.cup_mode && signal.volume_multiple != null && !fullscreen && (
                  <div className="text-xs font-mono text-secondary-fg">
                    Breakout volume <span className="text-pos">{signal.volume_multiple}×</span> avg
                  </div>
                )}
              </div>

              {/* Indicator toolbar — overlay + sub-pane indicators. Click
                  toggles. Chart redraws only the selected ones to keep
                  performance light. Selection persists across reloads via
                  localStorage. */}
              <div
                className="flex items-center gap-1.5 flex-wrap mb-2"
                data-testid="chart-indicator-toolbar"
              >
                <IndicatorMenu
                  active={activeIndicators}
                  onToggle={toggleIndicator}
                  onClear={() => setActiveIndicators([])}
                />
                {activeIndicators.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowSettings((s) => !s)}
                      data-testid="indicator-settings-toggle"
                      title="Configure indicator parameters"
                      className={
                        "px-2 py-1 border rounded-sm text-[10px] font-mono uppercase tracking-widest transition " +
                        (showSettings
                          ? "bg-mod text-bg border-mod"
                          : "border-subtle text-secondary-fg hover:bg-surface-hover hover:border-mod/40")
                      }
                    >
                      ⚙ Settings
                    </button>
                    <button
                      type="button"
                      onClick={savePreset}
                      data-testid="indicator-save-preset"
                      title="Save the current indicator combo as a named preset"
                      className="px-2 py-1 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:bg-surface-hover hover:border-pos/40 transition"
                    >
                      ＋ Save Preset
                    </button>
                  </>
                )}
              </div>

              {/* Saved presets strip — one-click apply, × to delete. */}
              {presets.length > 0 && (
                <div
                  className="flex items-center gap-1 flex-wrap mb-2"
                  data-testid="indicator-presets-strip"
                >
                  <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg mr-1">
                    Presets
                  </span>
                  {presets.map((p) => (
                    <span
                      key={p.id}
                      className={
                        "inline-flex items-center gap-1 px-1.5 py-0.5 border rounded-sm " +
                        (p.is_default
                          ? "border-amber-500/60 bg-amber-500/10"
                          : "border-pos/30 bg-pos-soft/60")
                      }
                      data-testid={`indicator-preset-${p.id}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleDefaultPreset(p.id)}
                        title={p.is_default ? "Default — auto-applies on new charts (click to unset)" : "Set as default (auto-applies on new charts)"}
                        className={
                          "text-[10px] leading-none transition " +
                          (p.is_default ? "text-amber-400" : "text-muted-fg hover:text-amber-400")
                        }
                        data-testid={`indicator-preset-default-${p.id}`}
                      >
                        {p.is_default ? "★" : "☆"}
                      </button>
                      <button
                        type="button"
                        onClick={() => applyPreset(p)}
                        title={`Apply: ${p.indicators.join(" · ")}`}
                        className={
                          "text-[10px] font-mono uppercase tracking-widest hover:opacity-80 " +
                          (p.is_default ? "text-amber-400" : "text-pos")
                        }
                        data-testid={`indicator-preset-apply-${p.id}`}
                      >
                        {p.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => deletePreset(p.id)}
                        title="Delete preset"
                        className="text-[9px] font-mono text-muted-fg hover:text-neg"
                        data-testid={`indicator-preset-delete-${p.id}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Indicator settings panel — shows compact number inputs for
                  each currently-active indicator's tunable params. Changes
                  apply instantly (chart re-renders with new params) and
                  persist in localStorage. */}
              {showSettings && activeIndicators.length > 0 && (
                <div
                  className="mb-3 p-3 border border-subtle rounded-sm bg-surface/60"
                  data-testid="indicator-settings-panel"
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
                    {[
                      { k: "ema20", label: "EMA 20", fields: [{ f: "period", def: 20, min: 2, max: 200 }] },
                      { k: "ema50", label: "EMA 50", fields: [{ f: "period", def: 50, min: 2, max: 200 }] },
                      { k: "ema200", label: "EMA 200", fields: [{ f: "period", def: 200, min: 2, max: 500 }] },
                      { k: "bb", label: "Bollinger Bands", fields: [{ f: "period", def: 20, min: 2, max: 100 }, { f: "mult", def: 2, step: 0.1, min: 0.5, max: 5 }] },
                      { k: "donchian", label: "Donchian", fields: [{ f: "period", def: 20, min: 2, max: 200 }] },
                      { k: "supertrend", label: "SuperTrend", fields: [{ f: "period", def: 10, min: 2, max: 50 }, { f: "mult", def: 3, step: 0.1, min: 0.5, max: 10 }] },
                      { k: "rsi", label: "RSI", fields: [
                        { f: "period", def: 14, min: 2, max: 50 },
                        { f: "overbought", def: 70, min: 51, max: 99 },
                        { f: "oversold", def: 30, min: 1, max: 49 },
                      ] },
                      { k: "macd", label: "MACD", fields: [{ f: "fast", def: 12, min: 2, max: 50 }, { f: "slow", def: 26, min: 3, max: 100 }, { f: "signal", def: 9, min: 2, max: 50 }] },
                      { k: "adx", label: "ADX", fields: [{ f: "period", def: 14, min: 2, max: 50 }] },
                      { k: "atr", label: "ATR", fields: [{ f: "period", def: 14, min: 2, max: 50 }] },
                    ]
                      .filter((it) => activeIndicators.includes(it.k))
                      .map((it) => {
                        const customised =
                          indicatorParams[it.k] &&
                          it.fields.some((f) => indicatorParams[it.k][f.f] != null && indicatorParams[it.k][f.f] !== f.def);
                        return (
                          <div key={it.k} className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-mono uppercase tracking-widest text-secondary-fg">
                                {it.label}
                              </span>
                              {customised && (
                                <button
                                  type="button"
                                  onClick={() => resetParams(it.k)}
                                  data-testid={`indicator-reset-${it.k}`}
                                  className="text-[9px] font-mono uppercase tracking-widest text-muted-fg hover:text-mod"
                                >
                                  reset
                                </button>
                              )}
                            </div>
                            <div className="flex gap-1.5">
                              {it.fields.map((f) => {
                                const cur = indicatorParams[it.k]?.[f.f] ?? f.def;
                                return (
                                  <label
                                    key={f.f}
                                    className="flex-1 flex flex-col gap-0.5"
                                  >
                                    <span className="text-[9px] font-mono uppercase tracking-widest text-muted-fg/80">
                                      {f.f}
                                    </span>
                                    <input
                                      type="number"
                                      value={cur}
                                      step={f.step || 1}
                                      min={f.min}
                                      max={f.max}
                                      onChange={(e) => updateParam(it.k, f.f, e.target.value)}
                                      data-testid={`indicator-param-${it.k}-${f.f}`}
                                      className="w-full px-2 py-1 border border-subtle rounded-sm bg-surface text-primary-fg font-mono text-xs tabular-nums focus:outline-none focus:border-mod"
                                    />
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                  {/* Show a hint when none of the active indicators are configurable
                      (e.g. only VWAP / Pivots / Auto-Fib / OBV are on). */}
                  {!["ema20", "ema50", "ema200", "bb", "donchian", "supertrend", "rsi", "macd", "adx", "atr"].some((k) => activeIndicators.includes(k)) && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg/70">
                      Selected indicators have no tunable parameters.
                    </div>
                  )}
                </div>
              )}
              {/* Chart area — splits into chart + analysis side-panel
                  when AI analysis is active so the candles never get
                  pushed below the fold. */}
              <div
                className={
                  "mt-2 " +
                  ((analysis || analysisLoading || analysisError)
                    ? "flex flex-col xl:flex-row gap-3"
                    : "")
                }
              >
                <div className="flex-1 min-w-0">
                  {ohlcLoading && !ohlc ? (
                    <div className={(fullscreen ? "flex-1" : "h-[440px]") + " flex items-center justify-center border border-subtle rounded-sm bg-surface"}>
                      <CircleNotch size={16} weight="bold" className="animate-spin text-mod" />
                      <span className="ml-2 text-sm text-secondary-fg">Loading candles…</span>
                    </div>
                  ) : ohlc?.bars?.length ? (
                    <div className="relative" data-testid="chart-drawing-wrapper">
                      {marketOpen && (
                        <div
                          className="absolute top-2 right-2 z-10 flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-bg/70 backdrop-blur-sm border border-pos/40 pointer-events-none"
                          data-testid="chart-live-badge"
                          title="NSE market is open · today's candle is live-updating"
                        >
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-pos opacity-75 animate-ping" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pos" />
                          </span>
                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-pos">
                            Live
                          </span>
                        </div>
                      )}
                      <CandleChart
                        bars={ohlc.bars}
                        ema10={ohlc.ema10}
                        entry={signal.fno_only ? null : signal.entry}
                        stopLoss={signal.fno_only ? null : stop_loss}
                        target={signal.fno_only ? null : target}
                        cupOverlay={
                          signal.cup_mode
                            ? {
                                rim: signal.right_rim,
                                cupLow: signal.cup_low,
                                leftRimDate: signal.left_rim_date,
                                rightRimDate: signal.right_rim_date,
                                cupLowDate: signal.cup_low_date,
                            breakoutDate: signal.breakout_date,
                          }
                        : null
                    }
                    emaCrossOverlay={!!signal.ema_cross_mode}
                    goldenCrossOverlay={!!signal.golden_cross_mode}
                    niftyBars={withNifty ? niftyOhlc?.bars : null}
                    sectorBars={withSector ? sectorOhlc?.bars : null}
                    sectorName={sectorMeta?.name}
                    height={fullscreen ? Math.max(360, (typeof window !== "undefined" ? window.innerHeight : 800) - 120) : 440}
                    onReady={(ctx) => { chartCtxRef.current = ctx; }}
                    indicators={activeIndicators}
                    indicatorParams={indicatorParams}
                    srLevels={srLevels}
                    symbol={signal.symbol}
                    companyName={signal.company_name}
                  />
                  <ChartDrawingOverlay
                    chartRef={chartCtxRef}
                    activeTool={activeTool}
                    drawings={drawings}
                    onAddDrawing={(d) => setDrawings((arr) => [...arr, d])}
                    onSetActiveTool={setActiveTool}
                    height={fullscreen ? Math.max(360, (typeof window !== "undefined" ? window.innerHeight : 800) - 120) : 440}
                    bars={ohlc?.bars}
                  />
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center border border-subtle rounded-sm bg-surface text-sm text-muted-fg">
                  Chart data unavailable.
                </div>
              )}
                </div>
                {/* AI Chart Analysis side panel — slides in on the right,
                    keeping the chart fully visible. Stacks vertically on
                    narrow viewports (< xl). */}
                {(analysis || analysisLoading || analysisError) && (
                  <div
                    className={
                      "xl:shrink-0 xl:max-h-[calc(100vh-280px)] xl:overflow-y-auto transition-[width] duration-200 " +
                      (analysisExpanded ? "xl:w-[560px]" : "xl:w-[400px]")
                    }
                  >
                    <ChartAnalysisPanel
                      data={analysis}
                      loading={analysisLoading}
                      error={analysisError}
                      generatedAt={analysisMeta?.generated_at}
                      cached={analysisMeta?.cached}
                      onClose={closeChartAnalysis}
                      onRefresh={() => runChartAnalysis(true)}
                      onSaveToWatchlist={saveAiNoteToWatchlist}
                      onTogglePin={togglePinLevel}
                      pinnedKeys={pinnedKeys}
                      watched={watched}
                      expanded={analysisExpanded}
                      onToggleExpand={() => setAnalysisExpanded((v) => !v)}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Exit rule explanation — only for breakout signals */}
            {!signal.fno_only && !signal.cup_mode && (
            <div
              className={
                "mt-6 p-4 rounded-sm border " +
                (signal.exit_alert
                  ? "bg-neg-soft border-neg-soft text-neg"
                  : "bg-pos-soft border-pos-soft text-pos")
              }
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                {signal.exit_alert ? (
                  <>
                    <Warning size={16} weight="bold" />
                    Exit triggered — last 2 candles are red and closed below EMA10.
                  </>
                ) : (
                  <>
                    <TrendUp size={16} weight="bold" />
                    Setup active. Exit rule: exit on 2 consecutive red candles closing below EMA10 (₹{fmt(signal.ema10)}).
                  </>
                )}
              </div>
            </div>
            )}

            {/* Cup-and-Handle exit rule banner (different from breakout exit rule) */}
            {signal.cup_mode && (
              <div
                className="mt-6 p-4 rounded-sm border bg-pos-soft border-pos-soft text-pos"
                data-testid="cup-exit-rule"
              >
                <div className="flex items-center gap-2 text-sm font-medium">
                  <TrendUp size={16} weight="bold" />
                  Setup active. Hold above ₹{fmt(signal.right_rim)} (rim).
                  Exit rule: close below ₹{fmt(signal.stop_loss)}{" "}
                  ({signal.kind === "long" ? "monthly" : "weekly"} candle low) — your stop-loss anchor.
                </div>
              </div>
            )}

            {/* Position Sizer — auto-uses Entry / SL from this signal.
                Hidden for F&O-only views (no entry/SL) since the sizer
                requires per-share risk. */}
            {!signal.fno_only && signal.entry != null && signal.stop_loss != null && (
              <div className="mt-6" data-testid="modal-position-sizer">
                <PositionSizer signal={signal} />
              </div>
            )}

            {/* AI Fundamentals */}
            <FundamentalsPanel symbol={signal.symbol} />

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                data-testid="modal-watch-button"
                onClick={() => onWatch?.(signal)}
                className="px-4 py-2 border border-subtle rounded-sm text-primary-fg hover:bg-surface-hover hover:border-focus transition text-sm"
              >
                {watched ? "Remove from Watchlist" : "Add to Watchlist"}
              </button>
              <a
                href={`https://in.tradingview.com/chart/?symbol=NSE%3A${signal.symbol.replace(".NS", "")}`}
                target="_blank"
                rel="noreferrer"
                data-testid="open-tradingview"
                className="px-4 py-2 rounded-sm text-sm font-medium flex items-center gap-2 transition"
                style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
              >
                Open in TradingView ↗
              </a>
              <a
                href={`https://www.nseindia.com/get-quotes/equity?symbol=${signal.symbol.replace(".NS", "")}`}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 border border-subtle rounded-sm text-primary-fg hover:bg-surface-hover hover:border-focus transition text-sm"
              >
                View on NSE ↗
              </a>
              <a
                href={`https://www.screener.in/company/${signal.symbol.replace(".NS", "")}/`}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 border border-subtle rounded-sm text-primary-fg hover:bg-surface-hover hover:border-focus transition text-sm"
              >
                Screener.in ↗
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
